import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import BusinessLayout from '../../components/business/BusinessLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import { fetchBusinessDashboard, fetchPendingReviewsCount, fetchReviewVelocity } from '../../api/business';

// ─── Helpers ───────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '–';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

// Compute review health score (0–100)
const computeHealthScore = (stats, pendingCount) => {
  if (!stats || !stats.totalReviews) return { score: 0, label: 'No Data', color: 'text-slate-400', bg: 'bg-slate-100' };
  const approvalRatio = stats.totalReviews > 0 ? (stats.approvedReviews || 0) / stats.totalReviews : 0;
  const ratingRatio = (stats.avgRating || 0) / 5;
  const pendingPenalty = Math.min(pendingCount / Math.max(stats.totalReviews, 1), 1) * 0.15;
  const score = Math.round(((approvalRatio * 0.5) + (ratingRatio * 0.5) - pendingPenalty) * 100);
  const clamped = Math.max(0, Math.min(100, score));
  if (clamped >= 80) return { score: clamped, label: 'Excellent', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
  if (clamped >= 60) return { score: clamped, label: 'Good', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
  if (clamped >= 40) return { score: clamped, label: 'Needs Attention', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
  return { score: clamped, label: 'Critical', color: 'text-red-700', bg: 'bg-red-50 border-red-200' };
};

const currentMonthName = () =>
  new Date().toLocaleDateString('en-US', { month: 'long' });

const lastMonthName = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString('en-US', { month: 'long' });
};

// ─── Sub-components ────────────────────────────────────────────────────────

const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24"
          fill={i <= full ? '#3b82f6' : (i === full + 1 && half ? '#93c5fd' : 'none')}
          stroke="#3b82f6" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
};

const StatCard = ({ icon, label, value, sub, color, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 transition-all duration-150
      ${onClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-sm hover:-translate-y-0.5 active:translate-y-0' : ''}`}
  >
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${color}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-1.5">{label}</p>
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1.5">{sub}</p>}
    </div>
    {onClick && (
      <svg className="text-slate-300 shrink-0 mt-1" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    )}
  </div>
);

const SectionHeader = ({ title, linkLabel, linkPath, navigate }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-[14px] font-semibold text-slate-900">{title}</h3>
    {linkLabel && (
      <button
        onClick={() => navigate(linkPath)}
        className="text-[12px] font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
      >
        {linkLabel}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    )}
  </div>
);

// Velocity trend indicator
const Trend = ({ current, previous, suffix = '' }) => {
  if (previous === 0 && current === 0) return <span className="text-[11px] text-slate-400">No change</span>;
  const diff = current - previous;
  if (diff === 0) return <span className="text-[11px] text-slate-500 font-medium">Same as before</span>;
  const isUp = diff > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points={isUp ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
      </svg>
      {Math.abs(diff)}{suffix} {isUp ? 'more' : 'fewer'} than {previous > 0 ? `${previous} ` : ''}before
    </span>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────

const BusinessDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [velocity, setVelocity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [data, pending, vel] = await Promise.all([
          fetchBusinessDashboard(),
          fetchPendingReviewsCount(),
          fetchReviewVelocity(),
        ]);
        setDashData(data);
        setPendingCount(pending.pendingCount);
        setVelocity(vel);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const stats = dashData?.stats;
  const business = dashData?.business;
  const recentReviews = dashData?.recentReviews ?? [];
  const ratingDist = stats?.ratingDistribution || {};
  const totalRatings = Object.values(ratingDist).reduce((a, b) => a + b, 0);
  const health = computeHealthScore(stats, pendingCount);

  return (
    <BusinessLayout title="Dashboard">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/business/dashboard' }]} />

      {/* ── Welcome Banner ── */}
      <div className="mb-6 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-[18px] font-bold text-slate-800">
              Welcome back, {user?.name?.split(' ')[0]} 👋
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2">
              <span className="font-medium text-slate-700">{business?.business_name || 'Your Business'}</span>
              {business?.city && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>{business.city}{business.country ? `, ${business.country}` : ''}</span>
                </>
              )}
              <span className="text-slate-300">·</span>
              <span>Member since {formatDate(business?.created_at)}</span>
            </p>
          </div>
          <button
            onClick={() => navigate('/business/reviews')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-[12px] font-semibold text-white hover:bg-blue-700 transition-all shadow-sm"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Manage Reviews
          </button>
        </div>
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      )}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-[13px]">{error}</div>
      )}

      {!loading && !error && dashData && (
        <>
          {/* ── 1. Pending Action Queue ── */}
          {pendingCount > 0 && (
            <button
              onClick={() => navigate('/business/reviews?status=pending')}
              className="w-full mb-5 flex items-center gap-4 px-5 py-4 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors group text-left"
            >
              {/* Pulse icon */}
              <div className="relative shrink-0">
                <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-[9px] font-bold text-white">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              </div>
              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-amber-900">
                  {pendingCount} review{pendingCount !== 1 ? 's' : ''} pending your action
                </p>
                <p className="text-[12px] text-amber-700 mt-0.5">
                  These reviews are awaiting approval and are not yet visible to customers. Review them now.
                </p>
              </div>
              {/* CTA */}
              <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-[12px] font-semibold text-white group-hover:bg-amber-600 transition-colors">
                Review Now
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
            </button>
          )}

          {/* ── 2. Stats Strip ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            <StatCard
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>}
              label="Total Reviews"
              value={stats?.totalReviews || 0}
              sub="All received"
              color="bg-blue-500"
              onClick={() => navigate('/business/reviews')}
            />
            <StatCard
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
              label="Pending Reviews"
              value={pendingCount}
              sub={pendingCount > 0 ? 'Awaiting approval' : 'All reviewed'}
              color={pendingCount > 0 ? 'bg-amber-500' : 'bg-slate-400'}
              onClick={() => navigate('/business/reviews?status=pending')}
            />
            <StatCard
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75"><polyline points="20 6 9 17 4 12" /></svg>}
              label="Approved Reviews"
              value={stats?.approvedReviews || 0}
              sub="Published"
              color="bg-emerald-500"
              onClick={() => navigate('/business/reviews?status=approved')}
            />
            <StatCard
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>}
              label="Avg Rating"
              value={(stats?.avgRating || 0).toFixed(1)}
              sub={`from ${stats?.totalReviews || 0} reviews`}
              color="bg-violet-500"
              onClick={() => navigate('/business/reviews')}
            />
          </div>

          {/* ── 3. Health Score + Review Velocity ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">

            {/* Review Health Score — compact, no fixed height */}
            <div className={`rounded-xl border p-5 ${health.bg}`}>
              {/* Header row: title + score side by side */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[14px] font-semibold text-slate-900">Review Health Score</h3>
                <div className="flex items-baseline gap-1">
                  <span className={`text-2xl font-extrabold leading-none ${health.color}`}>{health.score}</span>
                  <span className="text-[10px] font-semibold text-slate-400">/ 100</span>
                </div>
              </div>

              {/* Metrics block */}
              <div className="flex items-start gap-3">
                {/* Divider */}
                <div className="w-px self-stretch bg-black/10 shrink-0" />
                {/* Metrics */}
                <div className="flex-1">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold ${health.color} ${health.bg} border mb-2`}>
                    {health.label}
                  </span>
                  <div className="space-y-1 text-[12px] text-slate-600">
                    <div className="flex items-center gap-3 justify-between">
                      <span className="shrink-0">Approval ratio</span>
                      <span className="font-semibold text-slate-800">
                        {stats?.totalReviews ? Math.round(((stats.approvedReviews || 0) / stats.totalReviews) * 100) : 0}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3 justify-between">
                      <span className="shrink-0">Avg rating</span>
                      <span className="font-semibold text-slate-800">{(stats?.avgRating || 0).toFixed(1)} / 5.0</span>
                    </div>
                    <div className="flex items-center gap-3 justify-between">
                      <span className="shrink-0">Pending</span>
                      <span className={`font-semibold ${pendingCount > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {pendingCount} awaiting
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {health.score < 60 && (
                <p className="text-[11px] text-slate-600 pt-3 mt-3 border-t border-black/10">
                  💡 Approving pending reviews and a higher rating will improve your score.
                </p>
              )}
            </div>

            {/* Review Velocity — 4 compact tiles in one row */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
              <SectionHeader title="Review Velocity" navigate={navigate} />

              {velocity ? (
                <div className="grid grid-cols-4 gap-2">
                  {/* This Week */}
                  <div className="rounded-lg bg-blue-50 border border-blue-100 p-2.5">
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-tight">This Week</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 mb-1.5">Mon–today</p>
                    <p className="text-2xl font-extrabold text-blue-700 leading-none">{velocity.thisWeek}</p>
                    <div className="mt-1"><Trend current={velocity.thisWeek} previous={velocity.lastWeek} /></div>
                  </div>

                  {/* Last Week */}
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-tight">Last Week</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 mb-1.5">Mon–Sun</p>
                    <p className="text-2xl font-extrabold text-slate-700 leading-none">{velocity.lastWeek}</p>
                    <p className="text-[9px] text-slate-400 mt-1">received</p>
                  </div>

                  {/* This Month */}
                  <div className="rounded-lg bg-violet-50 border border-violet-100 p-2.5">
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-tight">{currentMonthName()}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 mb-1.5">This month</p>
                    <p className="text-2xl font-extrabold text-violet-700 leading-none">{velocity.thisMonth}</p>
                    <div className="mt-1"><Trend current={velocity.thisMonth} previous={velocity.lastMonth} /></div>
                  </div>

                  {/* Last Month */}
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-tight">{lastMonthName()}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 mb-1.5">Prev month</p>
                    <p className="text-2xl font-extrabold text-slate-700 leading-none">{velocity.lastMonth}</p>
                    <p className="text-[9px] text-slate-400 mt-1">received</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-slate-400 text-[13px]">
                  Unable to load velocity data
                </div>
              )}
            </div>
          </div>

          {/* ── 4. Rating Breakdown + Recent Reviews ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Rating Breakdown */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 h-[365px] flex flex-col">
              <SectionHeader
                title="Rating Breakdown"
                linkLabel="All Reviews"
                linkPath="/business/reviews"
                navigate={navigate}
              />
              <div className="flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                {/* Average hero */}
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-100">
                  <div className="text-5xl font-extrabold text-slate-900 leading-none tabular-nums">
                    {(stats?.avgRating || 0).toFixed(1)}
                  </div>
                  <div>
                    <StarRating rating={stats?.avgRating || 0} />
                    <p className="text-[12px] text-slate-500 mt-1 font-medium">
                      {stats?.totalReviews || 0}&nbsp;{stats?.totalReviews === 1 ? 'review' : 'reviews'} total
                    </p>
                  </div>
                </div>
                {/* Star rows */}
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = ratingDist[star] || 0;
                    const pct = totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
                    const badgeCls =
                      star >= 4 ? 'bg-emerald-100 text-emerald-700'
                        : star === 3 ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-600';
                    return (
                      <button
                        key={star}
                        onClick={() => navigate(`/business/reviews?rating=${star}`)}
                        className="w-full flex items-center gap-3 py-2 px-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <span className="flex items-center gap-1 text-[13px] font-medium text-slate-700 w-16 shrink-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                          {star} star{star !== 1 ? 's' : ''}
                        </span>
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold ${badgeCls}`}>{count}</span>
                        <span className="ml-auto text-[11px] text-slate-400 font-medium">{pct}%</span>
                        <svg className="text-slate-300 group-hover:text-slate-400 transition-colors shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Reviews */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 h-[365px] flex flex-col">
              <SectionHeader
                title="Recent Reviews"
                linkLabel="View All"
                linkPath="/business/reviews"
                navigate={navigate}
              />
              {recentReviews.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.25" className="mb-3">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <p className="text-[13px] text-slate-400 font-medium">No reviews yet</p>
                  <p className="text-[12px] text-slate-400 mt-0.5">Reviews from customers will appear here</p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto divide-y divide-slate-50 -mx-1 px-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                  {recentReviews.slice(0, 5).map((review, idx) => (
                    <button
                      key={review.id || idx}
                      onClick={() => navigate('/business/reviews')}
                      className="w-full text-left flex items-start gap-3 py-3.5 px-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-[12px] font-bold text-white">
                        {(review.reviewer_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-[13px] font-semibold text-slate-900">{review.reviewer_name || '–'}</span>
                          <StarRating rating={review.rating || 0} />
                          <span className="text-[12px] font-medium text-slate-500">{review.rating}</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${review.is_approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {review.is_approved ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                        {review.title && <p className="text-[12px] font-medium text-slate-700 truncate">{review.title}</p>}
                        {review.content && (
                          <p className="text-[11.5px] text-slate-400 line-clamp-1 mt-0.5">
                            {review.content.substring(0, 110)}{review.content.length > 110 ? '…' : ''}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <p className="text-[11px] text-slate-400">{timeAgo(review.created_at)}</p>
                        <svg className="text-slate-300 group-hover:text-blue-400 transition-colors" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </BusinessLayout>
  );
};

export default BusinessDashboard;
